import { Component, createMemo, onCleanup } from 'solid-js'
import Select from '../../../shared/Select'
import { FormLabel } from '../../../shared/FormLabel'
import { userStore } from '../../../store/user'
import { UI } from '/common/types'
import FileInput, { FileInputResult } from '../../../shared/FileInput'
import ColorPicker from '../../../shared/ColorPicker'
import Button from '../../../shared/Button'
import { X } from 'lucide-solid'
import { createDebounce } from '../../../shared/util'
import { InlineRangeInput } from '../../../shared/RangeInput'
import { FONT_FACES } from '/common/types/ui'
import { i18nStore } from '../../../store'

const themeOptions = UI.UI_THEME.map((color) => ({ label: color, value: color }))

export const ThemeUISettings: Component = (props) => {
  const state = userStore((s) => ({ ui: s.ui, current: s.current, background: s.background }))
  const i18n = i18nStore((s) => ({ t: s.t }))

  const [tryCustomUI, unsubCustomUi] = createDebounce((update: Partial<UI.CustomUI>) => {
    userStore.tryCustomUI(update)
  }, 50)

  const fonts = createMemo(() => {
    const options = Object.entries(FONT_FACES).map(([key, value]) => ({
      label: value.label,
      value: key,
    }))

    return options
  })

  onCleanup(() => unsubCustomUi())

  const themeBgOptions = createMemo(() => {
    const options = UI.BG_THEME.map((color) => ({ label: color as string, value: color as string }))
    const custom = state.current.bgCustom || ''
    if (custom !== '') return [{ label: i18n.t.settings_ui_custom, value: '' }]
    return options
  })

  const onBackground = async (results: FileInputResult[]) => {
    if (!results.length) return
    const [result] = results

    userStore.setBackground(result)
  }

  return (
    <>
      {' '}
      <div class="flex flex-row justify-start gap-4">
        <Select
          fieldName="theme"
          items={themeOptions}
          label={i18n.t.settings_ui_color}
          value={state.ui.theme}
          onChange={(item) => userStore.saveUI({ theme: item.value as any })}
        />

        <Select
          fieldName="mode"
          label={i18n.t.settings_ui_mode}
          items={[
            { label: i18n.t.settings_ui_dark, value: 'dark' },
            { label: i18n.t.settings_ui_light, value: 'light' },
          ]}
          value={state.ui.mode}
          onChange={(item) => userStore.saveUI({ mode: item.value as any })}
        />
      </div>
      <div class="flex gap-2">
        <InlineRangeInput
          label={i18n.t.settings_ui_siteFontSize}
          parentClass="w-full"
          value={state.ui.fontSize ?? 16}
          min={4}
          max={32}
          onChange={(ev) => userStore.tryUI({ fontSize: ev })}
          step={1}
        />
        <Button
          size="sm"
          class="!py-2"
          onClick={() => userStore.saveUI({ fontSize: state.ui.fontSize })}
        >
          {i18n.t.admin_apply}
        </Button>
      </div>
      <div class="flex flex-col">
        <FormLabel
          label={i18n.t.settings_ui_backgrounds}
          helperText={
            <>
              <span class="link" onClick={() => userStore.saveCustomUI({ bgCustom: '' })}>
                {i18n.t.settings_ui_resetToDefault}
              </span>
            </>
          }
        />
        <div class="flex items-center gap-2">
          <Select
            fieldName="themeBg"
            inline
            label={i18n.t.settings_ui_backgroundColor}
            items={themeBgOptions()}
            value={state.ui.themeBg}
            onChange={(item) => userStore.saveUI({ themeBg: item.value })}
          />
          <ColorPicker
            fieldName="customBg"
            onChange={(color) => userStore.saveCustomUI({ bgCustom: color })}
            onInput={(color) => tryCustomUI({ bgCustom: color })}
            value={state.current.bgCustom ?? state.ui[state.ui.mode].bgCustom}
          />
        </div>
      </div>
      <div class="flex items-end gap-2">
        <FileInput
          fieldName="background"
          label={i18n.t.settings_ui_backgroundImage}
          onUpdate={onBackground}
          accept="image/png,image/jpeg,image/jpg"
        />
        <div class="w-full justify-center">
          <Button disabled={!state.background} onClick={() => userStore.setBackground(null)}>
            <X size={16} />
            {i18n.t.settings_ui_remove}
          </Button>
        </div>
      </div>
      <Select
        fieldName="font"
        label={i18n.t.settings_ui_font}
        items={fonts()}
        value={state.ui.font}
        onChange={(item) => userStore.saveUI({ font: item.value as any })}
      />
    </>
  )
}
